import { inject, Injectable, signal } from "@angular/core";
import { AmazonInvoice, AmazonInvoiceStatus } from "@front/m19-api-client";
import { AccountMarketplaceService, AccountSelectionService, BillingService } from "@front/m19-services";
import { map, of, shareReplay, switchMap, tap } from "rxjs";

@Injectable()
export class AmazonInvoicesService {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly billingService = inject(BillingService);

  public readonly loading = signal(true);

  public readonly amazonInvoices = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => {
      if (
        am.dspAdvertiser === undefined ||
        !AccountMarketplaceService.m19SeatProfileIds.includes(am.dspAdvertiser.profileId)
      ) {
        return of([]);
      }
      return this.getOrganizationAmazonInvoices(am.resourceOrganizationId).pipe(
        map((invoices) => invoices.get(am.dspAdvertiserId ?? "") ?? []),
      );
    }),
    tap(() => this.loading.set(false)),
    shareReplay(1),
  );

  public readonly pendingInvoices = this.amazonInvoices.pipe(
    map((invoices) => invoices.filter(AmazonInvoicesService.isPendingInvoice)),
  );
  public readonly overdueInvoices = this.pendingInvoices.pipe(
    map((invoices) => invoices.filter(AmazonInvoicesService.isOverdueInvoice)),
  );

  public getOrganizationAmazonInvoices(organizationId: number) {
    return this.billingService.getM19SeatAmazonInvoices(organizationId);
  }

  public static isPendingInvoice(invoice: AmazonInvoice) {
    return invoice.status !== AmazonInvoiceStatus.PAID_IN_FULL && invoice.status !== AmazonInvoiceStatus.WRITTEN_OFF;
  }

  public static isOverdueInvoice(invoice: AmazonInvoice) {
    return AmazonInvoicesService.isPendingInvoice(invoice) && Date.parse(invoice.dueDate) < Date.now();
  }
}
